/**
 * QR Code Display Page
 *
 * This component displays the QR code for customers to scan
 * and provides instructions for scanning
 */
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Link as MuiLink,
    CircularProgress,
    Alert,
    Card,
    CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FMBlogo from "../../../assests/FMBlogo.png";
import { getQRCodes } from "../../../api/api";
import ScrollButtons from "../../../components/ScrollButtons";
import { getCurrentBranchId, getCurrentUser } from "../../../utils/auth";
import { getFrontendUrl } from "../../../config/config";

const QRCodeDisplayPage = () => {
    const [qrCode, setQrCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [logoUrl, setLogoUrl] = useState(FMBlogo);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const branchId = getCurrentBranchId();
    const currentUser = getCurrentUser();

    // Get dynamic logo based on logged-in user
    useEffect(() => {
        if (currentUser && currentUser.logo) {
            // Construct logo URL from storage
            const logoFilename = currentUser.logo;
            const dynamicLogoUrl = getFrontendUrl(`/storage/logos/${logoFilename}`);
            setLogoUrl(dynamicLogoUrl);
        } else {
            // Fallback to default logo if no user logo is available
            setLogoUrl(FMBlogo);
        }
    }, [currentUser]);

    useEffect(() => {
        const fetchQRCode = async () => {
            try {
                const response = await getQRCodes(branchId);
                if (response && response.length > 0) {
                    setQrCode(response[0]); // Get the first QR code for this branch
                } else {
                    setError("Ingen QR-kode funnet for denne avdelingen.");
                }
            } catch (err) {
                console.error("Failed to fetch QR code:", err);
                setError("Kunne ikke laste QR-kode.");
            } finally {
                setLoading(false);
            }
        };

        fetchQRCode();
    }, [branchId]);

    const handleBackToChoice = () => {
        navigate("/home/customer-choice");
    };

    const handleSmsOption = () => {
        navigate("/home/sms-customer-view");
    };

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
                sx={{ mt: { xs: 6, sm: 10 } }}
            >
                <Box mb={2}>
                    <img
                        src={logoUrl}
                        alt="Branch Logo"
                        style={{
                            width: isMobile ? 180 : 300,
                            maxWidth: "100%",
                        }}
                        onError={(e) => {
                            // Fallback to default logo if dynamic logo fails to load
                            e.target.src = FMBlogo;
                        }}
                    />
                </Box>

                <Typography
                    variant="h4"
                    color="primary"
                    textAlign="center"
                    mb={2}
                    sx={{ fontWeight: "bold" }}
                >
                    📱 Skann QR-koden
                </Typography>

                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    textAlign="center"
                    mb={4}
                    sx={{ maxWidth: 500 }}
                >
                    Hold kameraet over QR-koden for å registrere deg i køen
                </Typography>

                {loading ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary">
                            Laster QR-kode...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
                        {error}
                    </Alert>
                ) : qrCode && qrCode.qr_data_url ? (
                    <Card sx={{ mb: 4, maxWidth: 400, width: "100%" }}>
                        <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h6" color="primary" mb={2}>
                                {qrCode.branch_name}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mb: 2,
                                }}
                            >
                                <img
                                    src={qrCode.qr_data_url}
                                    alt="QR Code"
                                    style={{
                                        width: isMobile ? 200 : 250,
                                        height: isMobile ? 200 : 250,
                                        maxWidth: "100%",
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                    }}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={2}
                            >
                                Hold kameraet over QR-koden for å skanne
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Kode: <strong>{qrCode.code}</strong>
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <Alert severity="warning" sx={{ mb: 3, maxWidth: 400 }}>
                        Ingen QR-kode tilgjengelig
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        width: "100%",
                        maxWidth: 400,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleBackToChoice}
                        sx={{ flex: 1 }}
                    >
                        Tilbake
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSmsOption}
                        sx={{ flex: 1 }}
                    >
                        Bruk SMS i stedet
                    </Button>
                </Box>

                <Box mt={4} textAlign="center">
                    <Typography variant="body2">
                        <MuiLink href="#" underline="hover">
                            easyQue
                        </MuiLink>{" "}
                        (c) by Digifront
                    </Typography>
                    <Typography variant="body2" mt={1}>
                        <MuiLink
                            href="mailto:sales@digifront.biz"
                            underline="hover"
                        >
                            sales@digifront.biz
                        </MuiLink>
                    </Typography>
                </Box>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default QRCodeDisplayPage;
